import React, { useState, type MouseEvent } from 'react';
import { styled } from '@compiled/react';
import { type UIAnalyticsEvent, useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import { Flex } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { borderRadius, gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { useIssueKey, useIssueId } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { useIsIssueOfIncidentsPractice } from '@atlassian/jira-issue-field-servicedesk-practices/src/services/use-practices-field-value/index.tsx';
import {
	StakeholderUpdatesStoreContainer,
	useStakeholderUpdatesStore,
} from '@atlassian/jira-jsm-stakeholder-updates/src/services/index.tsx';
import { AsyncSendStakeholderUpdatesModal } from '@atlassian/jira-jsm-stakeholder-updates/src/ui/send-stakeholder-updates/async.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useProjectKey } from '@atlassian/jira-project-context-service/src/main.tsx';
import { useCanUseServiceDeskAgentFeatures } from '@atlassian/jira-project-permissions-service/src/main.tsx';
import { useIsJCS } from '@atlassian/jira-servicedesk-customer-service-common/src/utils/use-is-jcs/index.tsx';
import { useHasCurrentUserOpsgenieAccount } from '@atlassian/jira-servicedesk-incident-management-common/src/services/has-opsgenie-account/index.tsx';
import { DONE } from '@atlassian/jira-servicedesk-system-incident-status/src/services/constants.tsx';
import { useIncidentCapabilitiesProvisionState } from '@atlassian/jira-servicedesk-system-incident-status/src/services/main.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import messages from '../messages.tsx';
import type { CommentButtonActions } from '../types.tsx';
import AttachmentPicker from './attachment-picker/index.tsx';

export type OwnProps = {
	isPublicCommentAllowed: boolean;
	onReplyToCustomer: () => void;
	onAddInternalNote: () => void;
	replyToCustomerButtonActions?: CommentButtonActions;
	addInternalNoteButtonActions?: CommentButtonActions;
};

export type StateProps = {
	isCommentAttachmentInProgress: boolean;
};

export type Props = OwnProps & StateProps;

const UpdateStakeholderLinkInnerOld = () => {
	const [isStakeHolderModalOpen, setIsStakeHolderModalOpen] = useState(false);
	const { formatMessage } = useIntl();

	return (
		<>
			{' / '}
			<Button
				appearance="link"
				onClick={(event: MouseEvent, analyticsEvent: UIAnalyticsEvent) => {
					fireUIAnalytics(analyticsEvent, 'viewUpdateStakeholderModal');
					setIsStakeHolderModalOpen(true);
				}}
				interactionName="auto-generated.jira-issue-view-servicedesk-editor-container.collapsed-editor-view.button"
			>
				{formatMessage(messages.informStakeholders)}
			</Button>
			{isStakeHolderModalOpen && (
				<AsyncSendStakeholderUpdatesModal
					onCloseUpdateStakeholderModal={() => setIsStakeHolderModalOpen(false)}
				/>
			)}
		</>
	);
};

const UpdateStakeholderLinkInner = () => {
	const { formatMessage } = useIntl();
	const [isStakeHolderModalOpen, setIsStakeHolderModalOpen] = useState(false);
	const [{ limits, issueVersion }] = useStakeholderUpdatesStore();

	if (issueVersion.isNew) {
		const isLimitsExceed = limits.isEmailQuotaExceeded && limits.isTrial;

		return (
			<>
				&nbsp;/&nbsp;
				<Tooltip
					content={isLimitsExceed ? formatMessage(messages.informStakeholderLimitExceed) : ''}
				>
					<Button
						isDisabled={isLimitsExceed}
						appearance="link"
						onClick={(event: MouseEvent, analyticsEvent: UIAnalyticsEvent) => {
							fireUIAnalytics(analyticsEvent, 'viewUpdateStakeholderModal');
							setIsStakeHolderModalOpen(true);
						}}
						interactionName="auto-generated.jira-issue-view-servicedesk-editor-container.collapsed-editor-view.button1"
					>
						{formatMessage(messages.informStakeholders)}
					</Button>
				</Tooltip>
				{isStakeHolderModalOpen && (
					<AsyncSendStakeholderUpdatesModal
						onCloseUpdateStakeholderModal={() => setIsStakeHolderModalOpen(false)}
					/>
				)}
			</>
		);
	}

	return <UpdateStakeholderLinkInnerOld />;
};

export const UpdateStakeholderLink = () => {
	const issueKey = useIssueKey();
	const issueId = useIssueId();
	const projectKey = useProjectKey(issueKey);
	const isStakeholderVisible = useCanUseServiceDeskAgentFeatures(projectKey);
	const [hasOpsgenieAccount] = useHasCurrentUserOpsgenieAccount();
	const { cloudId } = useTenantContext();
	const [{ data: incidentCapabilities }] = useIncidentCapabilitiesProvisionState();
	const hasSystemIncident = incidentCapabilities?.provisionState === DONE;
	const showStakeholderUpdatesLink = isStakeholderVisible && hasOpsgenieAccount === true;
	const { createAnalyticsEvent } = useAnalyticsEvents();

	if (incidentCapabilities === undefined || !hasSystemIncident || !showStakeholderUpdatesLink) {
		return null;
	}

	return (
		<StakeholderUpdatesStoreContainer
			analyticsEvent={createAnalyticsEvent({})}
			issueId={issueId}
			cloudId={cloudId}
			scope={issueId}
		>
			<UpdateStakeholderLinkInner />
		</StakeholderUpdatesStoreContainer>
	);
};

export const CollapsedEditorView = ({
	onReplyToCustomer,
	onAddInternalNote,
	isCommentAttachmentInProgress,
	isPublicCommentAllowed,
	replyToCustomerButtonActions,
	addInternalNoteButtonActions,
}: Props) => {
	const { formatMessage } = useIntl();
	const isIssueOfIncidentsPractice = useIsIssueOfIncidentsPractice();
	const isJCS = useIsJCS();

	return (
		<ServiceDeskCommentButtonsContainer>
			{isCommentAttachmentInProgress ? (
				<LoadingAttachmentStatus>
					{formatMessage(messages.loadingAttachments)}
				</LoadingAttachmentStatus>
			) : (
				<Flex alignItems="center">
					<Button
						appearance="link"
						onClick={onAddInternalNote}
						interactionName="auto-generated.jira-issue-view-servicedesk-editor-container.collapsed-editor-view.button2"
						{...addInternalNoteButtonActions}
					>
						{formatMessage(
							isJCS && fg('jcs_issue_layout_eap') ? messages.addNote : messages.addInternalNote,
						)}
					</Button>
					{isPublicCommentAllowed && (
						<>
							&nbsp;/&nbsp;
							<Button
								appearance="link"
								onClick={onReplyToCustomer}
								interactionName="auto-generated.jira-issue-view-servicedesk-editor-container.collapsed-editor-view.button3"
								{...replyToCustomerButtonActions}
							>
								{formatMessage(messages.replyToCustomer)}
							</Button>
						</>
					)}
					{isIssueOfIncidentsPractice && <UpdateStakeholderLink />}
				</Flex>
			)}
			<AttachmentPicker />
		</ServiceDeskCommentButtonsContainer>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ServiceDeskCommentButtonsContainer = styled.div({
	whiteSpace: 'nowrap',
	borderWidth: '1px',
	borderStyle: 'solid',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	borderColor: `${token('color.border', colors.N40)}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 5}px`,
	boxSizing: 'border-box',
	paddingTop: token('space.050', '4px'),
	paddingRight: 0,
	paddingBottom: token('space.050', '4px'),
	paddingLeft: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${borderRadius}px`,
	display: 'flex',
	alignItems: 'center',
	overflow: 'hidden',
	flexWrap: 'wrap',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LoadingAttachmentStatus = styled.div({
	paddingTop: 0,
	paddingRight: token('space.100', '8px'),
	paddingBottom: 0,
	paddingLeft: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const visibleForTesting = {
	LoadingAttachmentStatus,
};
